import * as React from "react";
import { Table, Flex, Text } from "@fluentui/react-northstar";
import { gridNestedBehavior } from "@fluentui/accessibility";
import { ArrowUpIcon, ArrowDownIcon } from "@fluentui/react-icons-northstar";

type TableCellComparator<T> = (cell1: T, cell2: T) => number;

export interface TableColumnDefinition {
    headerTop?: JSX.Element;
    key: string;
    name: string;
    title: string;
    cellComparator?: TableCellComparator<any>;
}

interface AdvancedTableProps {
    rows: {}[];
    columns: TableColumnDefinition[];
}

export const stringCellComparator: TableCellComparator<string> = (cell1: string, cell2: string) => {
    if (cell1 && cell2 && typeof cell1 === "string" && typeof cell2 === "string") {
        return cell1.localeCompare(cell2);
    }
    return -1;
};

export const numberCellComparator: TableCellComparator<number | undefined> = (
    cell1: number | undefined,
    cell2: number | undefined
) => {
    if (cell1 === undefined && cell2 === undefined) {
        return 0;
    }

    if (cell1 === undefined) {
        return -1;
    }

    if (cell2 === undefined) {
        return 1;
    }
    return cell1 > cell2 ? 1 : cell1 === cell2 ? 0 : -1;
};

const AdvancedTable = (props: AdvancedTableProps) => {
    const { rows, columns } = props;

    const [sortColumn, setSortColumn] = React.useState("");
    const [sortDirection, setSortDirection] = React.useState(0);

    let sortedRows: {}[] = Object.assign([], rows);

    if (sortColumn && sortDirection) {
        const sortColumnIndex = columns.findIndex((column) => column.name === sortColumn);
        const comparator = columns[sortColumnIndex].cellComparator;
        if (comparator) {
            sortedRows = sortedRows.sort((r1: any, r2: any) => {
                const cell1 = r1.items[sortColumnIndex];
                const cell2 = r2.items[sortColumnIndex];
                return comparator(cell1.sortvalue, cell2.sortvalue) * sortDirection;
            });
        }
    }

    const sortColumnHeader = (
        headerTop: JSX.Element | undefined,
        title: string,
        key: string,
        order: number,
        onOrderChange: { (newSortDirection: any): void; (arg0: number): any }
    ) => ({
        content: (
            <Flex column fill>
                <div custom-role="headerTop">{headerTop}</div>
                <Flex gap="gap.small" style={{ paddingBottom: "15px" }}>
                    <Text content={title} />
                    {order !== 0 ? order === 1 ? <ArrowUpIcon size="smaller" /> : <ArrowDownIcon size="smaller" /> : ""}
                </Flex>
            </Flex>
        ),
        key: key,
        onClick: () => onOrderChange(order === 0 ? -1 : order === -1 ? 1 : 0),
        "aria-sort": order !== 0 ? (order > 0 ? "ascending" : "descending") : undefined,
    });

    const header = {
        key: "header",
        items: columns.map((c) =>
            sortColumnHeader(
                c.headerTop,
                c.title,
                c.key,
                c.name === sortColumn ? sortDirection : 0,
                (newSortDirection: number) => {
                    setSortColumn(c.name);
                    setSortDirection(newSortDirection);
                    // Use react-aria-live or similar component to announce change the order
                    if (document.getElementById("ariaLive")) {
                        document.getElementById("ariaLive")!.innerText =
                            newSortDirection > 0 ? "sorted ascending" : "sorted descending";
                    }
                }
            )
        ),
    };

    return (
        <Table
            variables={{ cellContentOverflow: "none" }}
            header={header}
            rows={sortedRows}
            accessibility={gridNestedBehavior}
        />
    );
};

export default AdvancedTable;
